import { useExperimentsMonitor, type ExperimentsMonitorManager } from '@/state/experiments/useExperimentsMonitor';
import { LineChart } from 'lucide-react';
import { EmptyState, Panel } from '../ConsolePanel';
import { FirstRunLink, FirstRunState } from '../FirstRunState';
import { Header } from './Header';
import { StatsCards } from './StatsCards';
import { TrendTable } from './TrendTable';
import { GroupedExperimentsTable } from './GroupedExperimentsTable';

export type { ExperimentsMonitorManager };

const DESCRIPTION = 'Conversion trends across every experiment you can reach.';

export function PerformanceScreen() {
  const manager = useExperimentsMonitor();
  const { isLoading, experiments, expandedTable } = manager;

  /*
    Three states used to leave the route rendering nothing at all — `return null` for
    loading, for a failed request and for an account with no experiments — so a reader
    could not tell a slow page from a broken one from an empty one. The monitor query
    has no prerequisite to gate on, so `!experiments` once loading is over is a failure.
  */
  if (isLoading) {
    return (
      <Panel title="Performance" description={DESCRIPTION}>
        <EmptyState>Loading experiment performance…</EmptyState>
      </Panel>
    );
  }

  if (!experiments) {
    return (
      <Panel title="Performance" description={DESCRIPTION}>
        <EmptyState>Could not load experiment performance. Reload the page to try again.</EmptyState>
      </Panel>
    );
  }

  if (experiments.length === 0) {
    return (
      <FirstRunState
        icon={LineChart}
        title="Nothing to compare yet"
        description="This page ranks your running experiments by how their conversions are trending. It fills in once an experiment has recorded its first visitors."
        action={<FirstRunLink to="/experiments/library">Go to experiments</FirstRunLink>}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <Header manager={manager} />
      <StatsCards manager={manager} />

      <div
        className={`grid gap-3.5 ${expandedTable ? 'grid-cols-1' : 'lg:grid-cols-2'}`}
        data-tour="performance-trends"
      >
        {(expandedTable === null || expandedTable === 'positive') && (
          <TrendTable manager={manager} trendType="positive" />
        )}
        {(expandedTable === null || expandedTable === 'negative') && (
          <TrendTable manager={manager} trendType="negative" />
        )}
      </div>

      <GroupedExperimentsTable manager={manager} />
    </div>
  );
}
